import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { ProductDetailPage } from '../components/ui/product-detail-page';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { FiTag, FiInfo, FiAward } from 'react-icons/fi';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, addToCart, trackEvent } = useStore();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const sourceProduct = products.find(p => p.id === id);

  // Track product view when product loads
  useEffect(() => {
    if (sourceProduct && trackEvent) {
      trackEvent('product_view', { product_id: sourceProduct.id });
    }
  }, [sourceProduct?.id, trackEvent]);

  if (!sourceProduct) {
    return (
      <div className="min-h-screen bg-brand-sand flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-5xl font-serif text-brand-darkSage mb-4">Product Not Found</h1>
        <p className="text-brand-lightText mb-8">The item you're looking for doesn't exist or has been removed.</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-brand-darkSage text-white px-8 py-3 rounded-full uppercase tracking-widest text-sm font-semibold hover:bg-brand-gold transition-colors"
        >
          Return Home
        </button>
      </div>
    );
  }

  // Build the images array (ensure no duplicates or empty values)
  const productImages = [
    sourceProduct.image,
    ...(sourceProduct.additional_images || [])
  ].filter(Boolean);

  // Format data to match the Shadcn ProductDetailPage requirements
  const formattedProduct = {
    name: sourceProduct.name,
    price: sourceProduct.price,
    shippingCost: 500.00, // Mock fixed shipping
    currency: "Rs. ",
    images: productImages,
    description: sourceProduct.description || "Experience the finest quality authentic Sri Lankan product, sourced sustainably and crafted with care to elevate your wellness routine.",
    tags: [
      { label: sourceProduct.category, icon: FiTag },
      { label: "100% Natural", icon: FiAward },
      { label: "Export Quality", icon: FiInfo },
    ],
  };

  const seller = {
    name: "Ceylanthro Official",
    avatarUrl: "https://media-manager-c.questera.ai/greta-media/29d79c4109d1c19c2e75790b8e50872c9a33c9d5ce79f6f43f60effe48b91ada6d4fe9244bdd866a6b5eb503d1d2a9a7/images/aW1hZ2UvcG5n/154eff52dc1f1c00de3fe58bdd4b1694.png",
    rating: 5.0,
  };

  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: sourceProduct.category, href: "/shop" },
    { label: sourceProduct.name, href: "#" },
  ];

  // Mock related products based on store context
  const relatedProducts = products
    .filter(p => p.id !== id)
    .slice(0, 4);

  return (
    <div className="bg-brand-sand min-h-screen pt-24">
      <Navbar />
      
      <div className="max-w-screen-xl mx-auto p-4 sm:p-6 md:p-12 mt-8">
        <ProductDetailPage 
          product={formattedProduct} 
          seller={seller} 
          breadcrumbs={breadcrumbs}
          onAddToCart={() => addToCart(sourceProduct)}
        />

        {/* "You might also like" section */}
        <div className="px-4 md:px-8 py-16 mt-12 bg-white/30 backdrop-blur-sm rounded-3xl border border-white/40 shadow-sm">
          <h2 className="text-3xl font-serif text-brand-darkSage mb-8 text-center">You might also like</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {relatedProducts.map((item) => (
              <div 
                key={item.id} 
                onClick={() => navigate(`/product/${item.id}`)}
                className="group cursor-pointer flex flex-col items-center"
              >
                <div className="bg-brand-darkSand/20 rounded-2xl aspect-square w-full overflow-hidden p-6 flex items-center justify-center border border-transparent group-hover:border-brand-gold/30 transition-all duration-300">
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-full object-contain filter drop-shadow-lg group-hover:scale-110 transition-transform duration-500" 
                  />
                </div>
                <h3 className="mt-4 font-serif text-brand-darkSage text-center font-semibold line-clamp-1">{item.name}</h3>
                <p className="text-brand-gold text-sm font-bold mt-1">Rs. {item.price.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProductPage;